const API_URL = process.env.REACT_APP_BACKEND_URL;

const fullUrl = (path) => {
    return `${API_URL}${path}`;
};

export const api = {
    auth: {
        signUp: fullUrl("/auth/signup"),
        check: fullUrl("/auth/check"),
        signIn: fullUrl("/auth/signin"),
        whoami: fullUrl("/auth/whoami"),
        goal: fullUrl("/auth/onboarding/goal"),
        bio: fullUrl("/auth/onboarding/bio"),
        bioText: fullUrl("/auth/onboarding/bioText"),
        niche: fullUrl("/auth/onboarding/niche"),
        socialLinks: fullUrl("/auth/onboarding/social-links"),
        template: fullUrl("/auth/onboarding/template"),
        verifyEmail: (token) => fullUrl(`/auth/verify-email?token=${token}`),
        resendVerification: fullUrl("/auth/resend-verification"),
        forgotPassword: fullUrl("/auth/forgot-password"),
        resetPassword: fullUrl("/auth/reset-password"),
    },
    shopAuth: {
        signIn: fullUrl("/shopAuth/signin"),
        whoami: fullUrl("/shopAuth/whoami"),
    },
    bookings: {
        create: fullUrl("/bookings/create-booking"),
        fetch: fullUrl("/bookings/bookings"),
        update: (id) => fullUrl(`/bookings/bookings/${id}`),
        updateBookingProduct: (id) => fullUrl(`/bookings/booking-products/${id}`),
        delete: (id) => fullUrl(`/bookings/bookings/${id}`),
        getById: (id) => fullUrl(`/bookings/bookings/${id}`),
        getByProductId: (id) => fullUrl(`/bookings/bookings-by-productId/${id}`),
        deleteBooking: fullUrl("/bookings/delete-booking-product/:id"),
    },
    twilio: {
        getVideoToken: fullUrl('/twilio/token'),
        leaveRoom: fullUrl('/twilio/room/leave'),
        checkRoom: fullUrl('/twilio/room'),
        createRoomRecord: fullUrl('/twilio/create-room'),
        requestJoin: fullUrl('/twilio/request-join'),
        acceptParticipant: fullUrl('/twilio/accept'),
        getParticipantToken: fullUrl('/twilio/get-token'),
        getRoomDetails: (roomName) => fullUrl(`/twilio/room/details/${roomName}`),

        getRoomByBooking: (bookingId) => fullUrl(`/twilio/room/booking/${bookingId}`),
        checkRoomStatus: (roomName) => fullUrl(`/twilio/room/${roomName}/status`),
        roomParticipants: (roomName) => fullUrl(`/twilio/room/${roomName}/participants`),
    },
    store: {
        userStoreData: (username) => fullUrl(`/store/${username}`),
        updateViewCount: (username) => fullUrl(`/store/${username}/view`),
        updateShopViewCount: (storeName) => fullUrl(`/store/${storeName}/shop-view`),
        updateProductClick: (storeName, productId, productType) => fullUrl(`/store/${storeName}/product/${productId}/click/${productType}`),
        checkout: (username, productSlug) => fullUrl(`/store/${username}/checkout/${productSlug}`),
        addEmailUser: (storeName) => fullUrl(`/store/${storeName}/emails`),
        createCustomerPaymentIntent: (storeName) => fullUrl(`/store/${storeName}/create-payment-intent`),
        onPaymentSuccess: (storeName) => fullUrl(`/store/${storeName}/payment-success`),
        processFreeBooking: (storeName) => fullUrl(`/store/${storeName}/process-free-booking`),
        getDownloadLink: (storeName) => fullUrl(`/store/${storeName}/download`),
        claimAccount: (storeName) => fullUrl(`/store/${storeName}/claim-account`),
        processFreeDownload: (storeName) => fullUrl(`/store/${storeName}/process-free-download`),
        fetchCommerceProducts: (storeName) => fullUrl(`/store/${storeName}/get-commerce-products`),

        //Stripe Sessions
        createDigitalCheckoutSession: fullUrl('/store/create-digital-checkout-session'),
        createBookingSession: fullUrl('/bookings/create-booking-session'),
        createTipSession: fullUrl('/bookings/create-tip-session'),

        //Digital product download via email link
        downloadProductByToken: (token) => fullUrl(`/store/download/${token}`),
        verifyDownloadToken: (token) => fullUrl(`/store/verify-download/${token}`),

        //On Success of Checkout for Digital Products
        verifyDigitalPayment: (sessionId) => fullUrl(`/store/verify-digital-payment/${sessionId}`),
        downloadDigitalProduct: (sessionId) => fullUrl(`/store/download-digital-product/${sessionId}`),

        //On Success of Checkout for Booking Products
        verifyBookingPayment: (sessionId) => fullUrl(`/store/verify-booking-payment/${sessionId}`),
        //On Success of Checkout for Tip Products
        verifyTipPayment: (sessionId) => fullUrl(`/bookings/verify-tip-payment/${sessionId}`),
        //On Success of Checkout for Engagement Products
        verifyEngagementPayment: (sessionId) => fullUrl(`/store/verify-engagement-payment/${sessionId}`),
        // Save buyer's details
        saveBuyerDetails: fullUrl('/store/save-buyer-details'),
    },
    engagement: {
        fetchUnfulfilled: fullUrl('/metrics/unfulfilled-engagement-orders'),
        fetchFulfilled: fullUrl('/metrics/fulfilled-engagement-orders'),
        updateFulfillment: (orderId) => fullUrl(`/metrics/${orderId}/fulfillment`),
        getDetails: (orderId) => fullUrl(`/api/engagement/orders/${orderId}/details`),
    },
    billing: {
        setup: () => fullUrl('/billing/intent/setup'),
        confirm: () => fullUrl('/billing/intent/confirm/no-redirect'),
        createSubscription: () => fullUrl('/billing/subscription/create'),
        createCheckoutSession: fullUrl('/billing/create-checkout-session'),
        customer: {
            createCheckoutSession: fullUrl('/customer-billing/create-checkout-session'),
            getSubscription: fullUrl('/customer-billing/subscription'),
            cancelSubscription: fullUrl('/customer-billing/subscription/cancel')
        },
        getBillingPlan: () => fullUrl('/billing/plan/get'),
        addOrUpdatePaymentMethod: () => fullUrl('/billing/card/add-or-update'),
        createAccountLink: () => fullUrl('/billing/account/create-account-link'),
        createPaypalAccountLink: () => fullUrl('/billing/account/create-paypal-account-link'),
        checkPaypalStatus: () => fullUrl('/billing/account/paypal-account-status'),
        updatePaypalMerchantId: () => fullUrl('/billing/account/update-paypal-merchant-id'),
        deletePaypalConnection: () => fullUrl('/billing/account/delete-paypal-connection'),
        deleteAccountLink: () => fullUrl('/billing/account/delete-account-link'),
        updateAccount: () => fullUrl('/billing/account/update-account-link'),
        cancelSubscription: () => fullUrl('/billing/subscription/cancel'),
        detachCard: fullUrl('/billing/card/detach'),
        requestCashout: fullUrl('/metrics/request-cashout'),
        checkVerification: () => fullUrl('/billing/account/check-verification'),
    },
    customers: {
        fetchCustomers: fullUrl("/customers/get-customers"),
        fetchEmails: fullUrl("/customers/get-emails"),
        fetchCustomerById: (id) => fullUrl(`/customers/get-customer/${id}`),
        fetchEmailById: (id) => fullUrl(`/customers/get-email/${id}`),
        login: fullUrl("/customers/auth/login"),
        signup: fullUrl("/customers/auth/signup"),
        register: fullUrl("/customers/auth/register"),
        whoami: fullUrl("/customers/whoami"),
        forgotPassword: fullUrl("/customers/auth/forgot-password"),
        resetPassword: fullUrl("/customers/auth/reset-password"),
        // Add new endpoints for customer profile management
        getProfile: fullUrl("/customers/profile"),
        updateProfile: fullUrl("/customers/profile"),
        getOrders: fullUrl("/customers/orders"),
        getUserImages: fullUrl("/customers/images"),
    },
    userProfile: {
        fetch: fullUrl("/auth/profile/user-profile"),
        update: fullUrl("/auth/profile/user-profile/:id"),
        updateDetails: fullUrl("/auth/profile/user-profile-details/:id"),
        updatePassword: fullUrl("/auth/profile/update-password"),
        updateAnalytics: fullUrl("/auth/profile/update-analytics"),
        updateAddress: fullUrl("/auth/profile/update-address"),
        updateOtherSettings: fullUrl("/auth/profile/update-other-settings"),
        fetchTheme: fullUrl("/auth/profile/theme"),
        updateDashboardVisit: fullUrl("/auth/profile/user-profile/update-dashboard-visit"),
        updateVerificationStatus: fullUrl("/auth/profile/update-verification"),
    },
    theme: {
        saveCardStyle: fullUrl("/auth/profile/save-card-style"),
        saveButtonStyle: fullUrl("/auth/profile/save-button-style"),
        saveBackground: fullUrl("/auth/profile/save-background"),
        uploadBackgroundImage: fullUrl("/auth/profile/upload-background-image"),
        uploadBackgroundVideo: fullUrl("/auth/profile/upload-background-video"),
        saveSelectedTheme: fullUrl("/auth/profile/save-selected-theme"),
        fetchSparkleTheme: fullUrl("/auth/profile/get-sparkle-theme"),
        saveImageStyle: fullUrl("/auth/profile/save-image-style"),
        saveFontStyle: fullUrl("/auth/profile/save-font-style"),
    },
    products: {
        create: fullUrl("/auth/store/create-product"),
        fetch: fullUrl("/auth/store/get-products"),
        fetchEdit: fullUrl("/auth/store/edit/:type/:id"),
        delete: fullUrl("/auth/store/delete-product/:id"),
        update: fullUrl("/auth/store/update-product/:type/:id"),
        updateCustom: fullUrl("/auth/store/update-custom-product/:id"),
        updateVideo: fullUrl("/auth/store/update-video-product/:id"),
        updateEmail: fullUrl("/auth/store/update-email-product/:id"),
        updateUrl: fullUrl("/auth/store/update-url-product/:id"),
        unpublish: fullUrl("/auth/store/unpublish-product/:id"),
        publish: fullUrl("/auth/store/publish-product/:id"),
        createCommerceProduct: fullUrl("/auth/store/create-commerce-product"),
        fetchCommerceProduct: fullUrl("/auth/store/commerce-product/:id"),
        updateCommerceProduct: fullUrl("/auth/store/update-commerce-product/:id"),
        deleteCommerceProduct: fullUrl("/auth/store/delete-commerce-product/:id"),

        //Ecommerce
        scrapeProduct: fullUrl("/auth/store/scrape-product"),
        updatePublishStatus: (id) => fullUrl(`/auth/store/update-product-publish-status/${id}`),
        fetchShopSettingsandMetrics: fullUrl("/auth/store/get-shop-settings"),
        updateShopSettings: fullUrl("/auth/store/update-shop-settings"),

        //spotify
        spotifyCreate: fullUrl("/auth/store/spotify/create"),
        spotifyUpdate: (id) => fullUrl(`/auth/store/spotify/update/${id}`),
        spotifyGet: (id) => fullUrl(`/auth/store/spotify/${id}`),
        spotifyMetadata: fullUrl("/auth/store/spotify/metadata"),

        //apple
        appleCreate: fullUrl("/auth/store/apple/create"),
        appleUpdate: (id) => fullUrl(`/auth/store/apple/update/${id}`),
        appleGet: (id) => fullUrl(`/auth/store/apple/${id}`),
        appleMetadata: fullUrl("/auth/store/apple/metadata"),

        //tip
        updateTipProduct: (id) => fullUrl(`/auth/store/update-tip-product/${id}`),

        //engagement
        updateEngagementProduct: fullUrl("/auth/store/update-product/:type/:id"),
        createEngagementSession: fullUrl('/auth/store/create-engagement-session'),
    },
    digitalProducts: {
        uploadImage: fullUrl('/auth/store/digital-products/input-form/upload-image'),
        uploadVideo: fullUrl('/auth/store/digital-products/input-form/upload-video'),
        fetchCustom3DProduct: (slug) => fullUrl(`/auth/store/physical-products/custom-3d-product/${slug}`),
        fetchPublicCustom3DProduct: (slug) => fullUrl(`/auth/store/public/physical-products/custom-3d-product/${slug}`),
        generateFinalImage: fullUrl('/auth/store/physical-products/generate-final-image'),
    },
    headlineSections: {
        create: fullUrl("/auth/store/create-headline"),
        fetch: fullUrl("/auth/store/get-headlines"),
        delete: fullUrl("/auth/store/delete-headline/:id"),
        update: fullUrl("/auth/store/update-headline/:id"),
    },
    block: {
        update: fullUrl("/auth/store/update-block-order")
    },
    orders: {
        fetchUnfulfilled: fullUrl("/metrics/unfulfilled-orders"),
        fetchFulfilled: fullUrl("/metrics/fulfilled-orders"),
        getFulfillmentDetails: (orderId) => fullUrl(`/metrics/orders/${orderId}/fulfillment-details`),
        updateFulfillment: (orderId) => fullUrl(`/metrics/orders/${orderId}/fulfillment`),
        getOrderDetails: (orderId) => fullUrl(`/metrics/orders/${orderId}`),
        uploadFulfillment: (orderId) => fullUrl(`/metrics/orders/${orderId}/upload`),
        getFulfillment: (token) => fullUrl(`/metrics/fulfillment/token/${token}`),
        downloadFulfillmentFile: (token, fileKey) => fullUrl(`/metrics/fulfillment/token/${token}/download/${fileKey}`),
        // Updated endpoints for digital downloads to use customers module
        getDownloadInfo: (orderId) => fullUrl(`/customers/orders/${orderId}/download-info`),
        downloadDigitalProduct: (orderId) => fullUrl(`/customers/orders/${orderId}/download`),
    },    
    metrics: {
        fetch: fullUrl("/metrics/get-metrics"),
        getShopMetrics: fullUrl("/shop/metrics"),
        fetchBalances: fullUrl('/metrics/balances'),
    },
    transactions: {
        fetch: fullUrl("/metrics/get-transactions"),
        fetchById: (id) => fullUrl(`/metrics/get-transaction/${id}`),
        refund: (id) => fullUrl(`/billing/customer/refund-transaction/${id}`),
        fetchCashoutHistory: fullUrl("/metrics/get-cashout-history"),
        retryCashout: (cashoutId) => fullUrl(`/metrics/retry-cashout/${cashoutId}`),

        //Hook this up
        checkStatus: (id) => `/api/transactions/${id}/status`,
        fetchByCashout: (cashoutId) => `/api/transactions/cashout/${cashoutId}`,
        fetchAvailable: '/api/transactions/available',
    },
    referrals: {
        fetch: fullUrl("/auth/profile/get-referral-data"),
    },
    services: {
        checkUserNameAvailablity: fullUrl("/auth/check-username"),
    },
    tools: {
        generate: fullUrl("/auth/generate-business-name"),
        domainCheck: fullUrl("/auth/generate-domain-name"),
        generateFreeImage: fullUrl("/auth/generate-image-generator"),
    },
    customize: {
        fetchImage: fullUrl("/auth/get-ai-image"),
        generateGptImage: fullUrl("/auth/customize/generateGptImage"),
    },
    shop: {
        // Public shop endpoints
        fetchCategories: fullUrl("/shop/categories"),
        fetchReadyMadeCategories: fullUrl("/shop/categories/ready-made"),
        fetchCollections: fullUrl("/shop/collections"),
        fetchProductsByCategory: (category) => fullUrl(`/shop/products/category/${category}`),
        fetchOrders: fullUrl("/shop/order-management/list"),
        fetchOrderById: (id) => fullUrl(`/shop/order-management/details/${id}`),
        getOrderStatistics: fullUrl("/shop/order-management/statistics"),
        updateShippingAddress: (id) => fullUrl(`/shop/order-management/details/${id}/shipping-address`),
        getOrderAuditLogs: (id) => fullUrl(`/shop/order-management/details/${id}/audit-logs`),
        uploadImage: fullUrl("/shop/upload-image"),
        upload3DModel: fullUrl("/shop/upload-3d-model"),
        
        // Admin shop endpoints
        createCategory: fullUrl("/shop/admin/categories"),
        getCategoryById: (id) => fullUrl(`/shop/admin/categories/${id}`),
        updateCategory: (id) => fullUrl(`/shop/admin/categories/${id}`),
        updateCategoryStatus: (id) => fullUrl(`/shop/admin/categories/${id}/status`),
        updateCategorySortOrder: (id) => fullUrl(`/shop/admin/categories/${id}/sort-order`),
        updateCategoriesSortOrder: fullUrl("/shop/admin/categories/bulk/sort-order"),
        getCollectionsForCategory: (id) => fullUrl(`/shop/admin/categories/${id}/collections`),
        deleteCategory: (id) => fullUrl(`/shop/admin/categories/${id}`),
        
        createCollection: fullUrl("/shop/admin/collections"),
        getCollectionById: (id) => fullUrl(`/shop/admin/collections/${id}`),
        updateCollection: (id) => fullUrl(`/shop/admin/collections/${id}`),
        deleteCollection: (id) => fullUrl(`/shop/admin/collections/${id}`),
        updateCollectionStatus: (id) => fullUrl(`/shop/admin/collections/${id}/status`),
        updateCollectionsSortOrder: fullUrl("/shop/admin/collections/bulk/sort-order"),
        
        fetchProducts: fullUrl("/shop/admin/products"),
        updateProductsSortOrder: fullUrl("/shop/admin/products/bulk/sort-order"),
        createProduct: fullUrl("/shop/admin/products"),
        fetchProduct: (id) => fullUrl(`/shop/admin/products/${id}`),
        updateProduct: (id) => fullUrl(`/shop/admin/products/${id}`),
        generateDescription: fullUrl("/shop/admin/ai/generate-product-description"),
        deleteEcommerceProduct: (id) => fullUrl(`/shop/admin/products/delete-ecommerce/${id}`),        
        deleteProductImage: (productId) => fullUrl(`/shop/admin/products/${productId}/images`),
        duplicateProduct: (id) => fullUrl(`/shop/admin/products/${id}/duplicate`),
        updateOrderStatus: (id) => fullUrl(`/shop/order-management/details/${id}/status`),
        refundOrder: (id) => fullUrl(`/shop/order-management/details/${id}/refund`),
          // Creator management endpoints
        fetchCreators: fullUrl("/shop/creators"),
        fetchCreatorById: (id) => fullUrl(`/shop/creators/${id}`),
        fetchCreatorStats: fullUrl("/shop/creators/stats"),
        fetchCreatorProductSales: (id) => fullUrl(`/shop/creators/${id}/product-sales`),
        updateCreator: (id) => fullUrl(`/shop/creators/${id}`),
        
        // Customer management endpoints (admin)
        fetchCustomers: fullUrl("/shop/customers"),
        fetchCustomerById: (id) => fullUrl(`/shop/customers/${id}`),
        fetchCustomerOrders: (id) => fullUrl(`/shop/customers/${id}/orders`),
    },    
    shipping: {
        // EasyPost shipping endpoints
        testModeInfo: fullUrl("/shipping/test-mode-info"),
        validateAddress: fullUrl("/shipping/validate-address"),
        getRates: fullUrl("/shipping/rates"),
        createLabel: fullUrl("/shipping/label"),
        getTracking: (trackingCode, carrier) => fullUrl(`/shipping/tracking/${carrier}/${trackingCode}`),
        updateTracking: (orderId) => fullUrl(`/shipping/orders/${orderId}/tracking`),
        getLabel: (orderId) => fullUrl(`/shipping/orders/${orderId}/label`),
        getPackingSlip: (orderId) => fullUrl(`/shipping/orders/${orderId}/packing-slip`),
        cancelShipment: (orderId) => fullUrl(`/shipping/orders/${orderId}/cancel`),
        buyInsurance: (orderId) => fullUrl(`/shipping/orders/${orderId}/insurance`),
        refundLabel: (orderId) => fullUrl(`/shipping/orders/${orderId}/refund`),
    },
    creatorStore: {
        // Creator store endpoints - for creator product browsing
        fetchCategories: fullUrl("/creator-store/categories"),
        fetchCollections: (category) => fullUrl(`/creator-store/${category}`),
        fetchProductsForCollection: (category, collection) => fullUrl(`/creator-store/${category}/${collection}`),
        fetchProductsByCategory: (category) => fullUrl(`/creator-store/${category}`),
        fetchProduct: (category, collection, productSlug) => fullUrl(`/creator-store/${category}/${collection}/${productSlug}`),
        fetchDirectProduct: (category, productSlug) => fullUrl(`/creator-store/${category}/product/${productSlug}`),
    },
    googleMerchant: {
        authUrl: fullUrl('/google-merchant/auth-url'),
        exchangeToken: fullUrl('/google-merchant/exchange-token'),
        accountStatus: fullUrl('/google-merchant/account-status'),
        syncProducts: fullUrl('/google-merchant/sync-products'),
        products: fullUrl('/google-merchant/products'),
        settings: fullUrl('/google-merchant/settings'),
        disconnect: fullUrl('/google-merchant/disconnect'),
        metrics: fullUrl('/google-merchant/metrics'),
        oauthCallback: fullUrl('/auth/google/callback'),

        // Product-specific calls - removing the /api prefix since API_URL already includes it
        syncProduct: (productId) => fullUrl(`/google-merchant/products/sync-product/${productId}`),
        syncAllProducts: fullUrl('/google-merchant/products/sync-all-products'),
        productSyncStatus: fullUrl('/google-merchant/products/product-sync-status'),
        checkProductStatus: fullUrl('/google-merchant/products/check-product-status'),
        deleteProduct: (productId) => fullUrl(`/google-merchant/products/${productId}`),
        deleteVariant: (productId, variantId) => fullUrl(`/google-merchant/products/${productId}/variants/${variantId}`),
        toggleSync: (productId) => fullUrl(`/google-merchant/products/${productId}/toggle-sync`),

        // Fixed paths by removing the duplicate /api prefix
        productStatus: (productId) => fullUrl(`/google-merchant/products/${productId}/status`),
        deleteProductGMC: (productId) => fullUrl(`/google-merchant/products/${productId}/google-merchant`),
    },
    facebookFeed: {
        status: fullUrl('/facebook-feed/status'),
        xml: fullUrl('/facebook-feed/xml'),
        products: fullUrl('/facebook-feed/products'),
        settings: fullUrl('/facebook-feed/settings'),
        bulkUpdate: fullUrl('/facebook-feed/bulk-update'),
        toggleProduct: (productId) => fullUrl(`/facebook-feed/products/${productId}/toggle`)
    }, 
    tiktokFeed: {
        base: fullUrl('/tiktok-feed'),
        status: fullUrl('/tiktok-feed/status'),
        xml: fullUrl('/tiktok-feed/xml'),
        products: fullUrl('/tiktok-feed/products'),
        settings: fullUrl('/tiktok-feed/settings'),
        bulkUpdate: fullUrl('/tiktok-feed/bulk-update'),
        exportXlsx: fullUrl('/tiktok-feed/export/xlsx'),
        toggleProduct: (productId) => fullUrl(`/tiktok-feed/products/${productId}/toggle`)
    },
    ecommerceShop: {
        fetchCollections: (category) => fullUrl(`/shop/collections/shop/${category}`),
        fetchCategories: fullUrl("/shop/category/fetch-categories"),
        fetchProductsForCollection: (category, collection) => fullUrl(`/shop/collections/shop/${category}/${collection}`),
        fetchProductsForCustomCollection: (category, collection) => fullUrl(`/shop/collections/custom/${category}/${collection}`),
        fetchProductsByCategory: (category) => fullUrl(`/shop/products/category/${category}`),
        fetchProduct: (category, collection, productSlug) => fullUrl(`/shop/${category}/${collection}/${productSlug}`),
        fetchDirectProduct: (category, productSlug) => fullUrl(`/shop/${category}/product/${productSlug}`),
        addProductToCreatorStore: fullUrl('/store/creator/add-product'),
        deleteEcommerceProduct: (id) => fullUrl(`/store/delete-ecommerce/${id}`),
        fetchProductByCreator: (storeName, productSlug) => fullUrl(`/store/${storeName}/${productSlug}`),
        createCheckoutSession: fullUrl('/store/create-checkout-session'),
        createDirectCheckoutSession: fullUrl('/store/create-direct-checkout-session'),
        fetchOrderDetails: (sessionId) => fullUrl(`/store/checkout/success?session_id=${sessionId}`),        closeDialogBox: fullUrl('/store/close-dialog-box'),
        fetchDialogSettings: fullUrl('/store/dialog-settings'),
        fetchPublicProducts: fullUrl("/shop/public/products"), // New public API endpoint
        fetchShopProducts: fullUrl("/shop/public/shop-products"), // Public API endpoint for shop homepage exclusive products
    },
    public: {
        fetchProductsByCategory: (category) => fullUrl(`/shop/products/category/${category}`),
        fetchProduct: (category, collection, productSlug) => fullUrl(`/shop/${category}/${collection}/${productSlug}`),
    },
    designs: {
        fetchAll: fullUrl("/designs"),
        fetchByCategory: (categoryId) => fullUrl(`/designs?category=${categoryId}`),
        fetchFeatured: fullUrl("/designs?featured=true"),
        search: (query) => fullUrl(`/designs?search=${encodeURIComponent(query)}`),
        fetchCategories: fullUrl("/designs/categories"),
        createCategory: fullUrl("/designs/categories"),
        updateCategory: (id) => fullUrl(`/designs/categories/${id}`),
        deleteCategory: (id) => fullUrl(`/designs/categories/${id}`),
        getById: (id) => fullUrl(`/designs/${id}`),
        updateDesign: (id) => fullUrl(`/designs/${id}`),
        deleteDesign: (id) => fullUrl(`/designs/${id}`),
        uploadDesign: fullUrl("/designs/upload"),
    },
    version: {
        info: fullUrl("/version/info"),
        health: fullUrl("/version/health"),
    },
};

export default api;